import { Descriptions } from 'antd';
import { useMemo } from 'react';
import {
  ActionButton,
  DetailLayout,
  isLocked,
  LockedBy,
  ObjectHeader,
  ObjectType,
  Padding,
  Stack,
  TotalEntries,
  useCheckLockAndNavigate,
  useShowLockData,
  useSystemClassifierQuery,
  Spinner,
  useIsAdmin,
  InfoIconUrl,
  useSystemClassifierTranslations,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './SystemClassifierDetails.translations.json';
import styled from 'styled-components';
import { SystemClassifierBreadcrumbs } from './SystemClassifierBreadcrumbs';
import { useParams } from 'react-router-dom';
import { SystemClassifierItemTable } from './SystemClassifierItemTable';
import { path } from '../../paths';
import { useNavigateTo } from '../../paths/useNavigateTo';

const { addTranslations, useTranslation } = i18nNsFactory(
  'system-classifier-details',
);
addTranslations(translations);

export const SystemClassifierDetails = () => {
  const { t } = useTranslation();
  const { translateSystemClassifier } = useSystemClassifierTranslations();
  const params = useParams<{ id: string }>();
  const systemClassifierId = Number(params.id);
  if (isNaN(systemClassifierId))
    throw new Error('System classifier ID not provided');

  const { data: systemClassifier } = useSystemClassifierQuery({
    id: systemClassifierId,
  });

  const { lockData, refetchLockData } = useShowLockData(
    ObjectType.SystemClassifier,
    systemClassifierId,
  );
  const navigateToSystemClassifierEdit = useNavigateTo(
    path.systemClassifierEdit,
    { id: systemClassifierId },
  );
  const checkLockAndNavigateToEdit = useCheckLockAndNavigate(
    refetchLockData,
    lockData,
    navigateToSystemClassifierEdit,
  );

  const hasPermissionToManage = useIsAdmin();
  const translatedSystemClassifier = useMemo(
    () =>
      systemClassifier
        ? translateSystemClassifier(systemClassifier)
        : undefined,
    [systemClassifier, translateSystemClassifier],
  );

  if (!systemClassifier || !translatedSystemClassifier) {
    return <Spinner />;
  }

  return (
    <DetailLayout
      infoIconUrl={InfoIconUrl.SYSTEM_CLASSIFIER_DETAILS}
      type="detail"
      breadCrumb={
        <SystemClassifierBreadcrumbs data={translatedSystemClassifier} />
      }
      headerConfig={
        hasPermissionToManage
          ? {
              actions: (
                <ActionButton
                  disabled={isLocked(lockData)}
                  type="mainAction"
                  layoutType="detail"
                  onClick={() => checkLockAndNavigateToEdit()}
                />
              ),
            }
          : undefined
      }
      side={{
        header: (
          <Padding>
            <ObjectHeader
              upperText={translatedSystemClassifier.code}
              lowerText={translatedSystemClassifier.name}
            />
          </Padding>
        ),
        content: (
          <Padding>
            <Descriptions
              column={1}
              items={[
                {
                  key: 'userManaged',
                  label: t('userManaged.label'),
                  children: systemClassifier.userManaged ? t('yes') : t('no'),
                },
                {
                  key: 'description',
                  label: t('description.label'),
                  children: translatedSystemClassifier.description,
                },
              ]}
            />
          </Padding>
        ),
        footer: (
          <LockedBy
            id={systemClassifier.id}
            entity={systemClassifier}
            objectType={ObjectType.SystemClassifier}
          />
        ),
      }}
      footerContent={<TotalEntries count={systemClassifier.itemCount} />}
    >
      <Padding>
        <ContentWrapper>
          <SystemClassifierItemTable
            systemClassifierItems={
              translatedSystemClassifier.systemClassifierItems
            }
          />
        </ContentWrapper>
      </Padding>
    </DetailLayout>
  );
};

const ContentWrapper = styled(Stack)`
  width: 100%;
  max-width: 100em;
  height: 100%;
  align-self: center;
  align-items: center;
  margin: 0 auto;
`;
